import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api';

export interface Timezone {
  id: number;
  /** IANA identifier (e.g. "America/New_York") — the value stored and submitted as `iana_timezone`. */
  name: string;
  /** Human-readable label (e.g. "Eastern Time (US & Canada)"). */
  display_name: string;
  /** e.g. "-04:00" or "+05:30" */
  utc_offset: string;
  /** e.g. "GMT-4" */
  gmt_label: string;
  countries: string[];
  sort_order: number;
}

export interface TimezonesResponse {
  success: boolean;
  message: string;
  data: Timezone[];
  count: number;
}

export const TIMEZONES_QUERY_KEY = ['timezones'] as const;

/** Display label shown in dropdowns and detail views, e.g. "(GMT-4) Eastern Time (US & Canada)". */
export function timezoneOptionLabel(tz: Pick<Timezone, 'display_name' | 'gmt_label'>): string {
  return `(${tz.gmt_label}) ${tz.display_name}`;
}

/** Label for showing a time, e.g. "Pacific Timezone, GMT-7"; zones like "Alaska" stay as named. */
export function timezoneDisplayLabel(tz: Pick<Timezone, 'display_name' | 'gmt_label'>): string {
  const name = tz.display_name.replace(/\s+Time\s*\(US & Canada\)\s*$/i, ' Timezone');
  return `${name}, ${tz.gmt_label}`;
}

/** Only US timezones are offered. */
const TIMEZONE_COUNTRY = 'United States';

/** Shared, cached timezone catalog. The list is static, so it is fetched once per session. */
export const useTimezonesQuery = () => {
  return useQuery({
    queryKey: TIMEZONES_QUERY_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<TimezonesResponse>('/timezones/', {
        params: { country: TIMEZONE_COUNTRY },
      });
      return data.data ?? [];
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * Returns a resolver from IANA id to a display label like "Pacific Timezone, GMT-7".
 * Falls back to `fallback` (or the raw id) when the zone isn't in the catalog.
 */
export const useTimezoneLabel = () => {
  const { data } = useTimezonesQuery();
  return useCallback(
    (iana?: string | null, fallback?: string | null): string | null => {
      if (!iana) return fallback ?? null;
      const entry = data?.find((tz) => tz.name.toLowerCase() === iana.toLowerCase());
      return entry ? timezoneDisplayLabel(entry) : (fallback ?? iana);
    },
    [data],
  );
};
